/* eslint-disable */
// @flow

import * as React from 'react';
import MediaQuery from 'react-responsive';
import classnames from 'classnames';
import {
  Analytics,
  AnalyticsContext,
  AnalyticsLocationProvider,
  AnalyticsLocationConsumer,
  trackEvent,
} from 'latitude-analytics';
import { ANALYTICS } from '@latitude/core/utils/constants';
import { Box } from '@latitude/box';
import { BrowserSupport } from '@latitude/browser-support';
import HamburgerButton from './HamburgerButton';
import MenuItem from './MenuItem';
import TopMenuItem from './TopMenuItem';
import LogonButton from './LogonButton';
import LoginForm from './LoginForm';
import type { LoginFormDataType, CardType } from './LoginForm';
import { LoginModal, getLoginModalData } from './LoginModal';
import type { LoginModalData } from './LoginModal';
// import SearchButton from './SearchButton';
import SearchForm from './SearchForm';
import type { SearchFormDataType } from './SearchForm';
import {
  DESKTOP_CUTOFF_POINT,
  MOBILE_CUTOFF_POINT,
} from './BaseHeader.constant';
import { toggleBodyScrolling } from './BaseHeader.utils';
import './_base-header.scss';

/**
 * Custom types
 */
export type MenuItemType = {
  id: string,
  label: string,
  href?: string,
  icon?: string,
  target?: string,
  bgGrey?: boolean, // Grey background?
  labelOnly?: boolean, // Item is label only, not a link
  desktopOnly?: boolean, // Only show this item in desktop view
  mobileOnly?: boolean, // Only show this item in mobile view
  labelBold?: boolean, // Bold label?
};

type MenuColumnType = Array<MenuItemType>;

type TopMenuItemType = {
  id: string,
  label: string,
  items: Array<MenuColumnType>,
  banner?: string, // The Id of the marketing banner to be rendered
  href: string,
};

// This pane is only rendered in mobile view
type MobilePaneType = {
  items: Array<MenuItemType>,
};

export type NavTreeDataType = {
  items: Array<TopMenuItemType>,
  mobilePane: MobilePaneType,
};

export type BaseHeaderProps = {
  /** navigation data for mega menu */
  navTreeData: NavTreeDataType,
  /** credit card data for login form */
  cardTypesData: Array<CardType>,
  /** login form content data */
  loginFormData: LoginFormDataType,
  /** login modal popover data */
  loginModalsData: Array<LoginModalData>,
  /** search form data */
  searchFormData: SearchFormDataType,
  /** render prop for banners */
  renderBanner: (bannerId: string, key?: number) => ?React.Element<any>,
  /** render props for brand button */
  renderBrandButton: (isSearchOpen: boolean) => React.Element<any>,
  /** render optional image for login modal popovers */
  renderLoginModalImage?: (imageFileName: string) => ?React.Element<any>,
  /** custom log in button label text */
  logonButtonLabel?: string,
  showInsurancePane?: boolean,
  showLpayPane?: boolean,
  showLoansPane?: boolean,
  showCardsPane?: boolean,
  /** enable new image sourcing syntax */
  setImageModuleSyntax?: boolean,
};

type State = {
  isNavOpen: boolean,
  isSearchOpen: boolean,
  isLoginOpen: boolean,
  animateClose: boolean,
  isNavBackgroundVisible: boolean,
  activeMenuId: ?string, // id for active top menu item, null for n none active
  loginModalData: ?LoginModalData, // single login modal data from URL query param
  showLoginModal: boolean, // if login modal popover is displayed
};

/**
 * This serves as a base header component that can serve multiple sites via:
 * - passed in navigation data
 * - passed in brand button content (logo)
 * - passed in marketing banners in mega menus
 */
class BaseHeader extends React.Component<BaseHeaderProps, State> {
  // DOM ref elements
  // These HTML elements are used to exclude clicking on their corresponding elements from popup closing logic.
  // E.g. We close a Login dialog when a user clicks outside of it UNLESS the click was on Login button.
  // To do that we pass a Ref to the Login button into the Login component.
  // We do the same with the mega menu popup.
  hamburgerButton: null | HTMLElement; // HTML element of the hamburger button
  logonButtonElement: null | HTMLElement; // HTML element of the Logon button
  topMenuItems: null | HTMLElement; // HTML element of the top navigation bar

  state = {
    isNavOpen: false,
    isSearchOpen: false,
    animateClose: false,
    isLoginOpen: false,
    isNavBackgroundVisible: false,
    activeMenuId: null,
    loginModalData: null,
    showLoginModal: false,
  };

  constructor(props: BaseHeaderProps) {
    super(props);

    // element refs
    this.hamburgerButton = React.createRef();
    this.topMenuItems = React.createRef();
  }

  componentDidMount() {
    // check and set login modal data
    const loginModalData = getLoginModalData(this.props.loginModalsData);
    if (loginModalData) {
      this.setState({ showLoginModal: true, loginModalData });
    }

    // bind events
    document.addEventListener('keydown', this.handleKeyDown, false);
    document.addEventListener('mousedown', this.handleClick, false);
  }

  componentWillUnmount() {
    // unbind events
    document.removeEventListener('keydown', this.handleKeyDown, false);
    document.removeEventListener('mousedown', this.handleClick, false);
  }

  /*
   * Handle key down
   */
  handleKeyDown = (event: KeyboardEvent) => {
    // Close the popup on Esc key
    if (event.keyCode === 27) {
      this.closeActiveMenu();
    }
  };

  /*
   * Handle Click
   */
  handleClick = (ev: MouseEvent) => {
    const clickTarget = ev.target instanceof Node ? ev.target : null;
    // if we click outside the menu, close it
    if (
      this.hamburgerButton &&
      !this.hamburgerButton.contains(clickTarget) &&
      this.topMenuItems &&
      !this.topMenuItems.contains(clickTarget)
    ) {
      this.closeActiveMenu();
    }
  };

  /**
   * Close active menu
   */
  closeActiveMenu = () => {
    this.setState({
      isNavOpen: false,
      activeMenuId: null,
    });
  };

  /*
   * Handle mouse click on a menu item
   */
  handleMenuItemClick = (
    menuItem: MenuItemType | TopMenuItemType,
    analytics: Analytics,
    location: string
  ) => {
    const isActive = this.state.activeMenuId === menuItem.id;

    const getActionLabel = () => {
      let href = menuItem.href;

      if (href === undefined) {
        return 'interaction';
      }

      if (href.startsWith('http')) {
        return 'external-link';
      }

      return 'internal-link';
    };

    this.setState(
      (state: State) => {
        return {
          ...state,
          activeMenuId: isActive ? null : menuItem.id,
        };
      },
      () => {
        if (!isActive) {
          trackEvent(analytics, {
            category: menuItem.href !== undefined ? 'text-link' : 'megamenu',
            action: getActionLabel(),
            label: menuItem.label,
            location,
          });
        }
      }
    );
  };

  /**
   * Toggle mega menu navigation pane open/close
   */
  toggleNavOpen = (analytics: Analytics, location: string) => {
    /**
     * Hamburger button event tracking
     */
    if (trackEvent) {
      trackEvent(analytics, {
        category: 'nav',
        action: 'interaction',
        label: 'open',
        location,
      });
    }

    toggleBodyScrolling(this.state.isNavOpen);
    this.setState((state: State) => ({
      isNavOpen: !state.isNavOpen,
      activeMenuId: null,
      isLoginOpen: false,
      isSearchOpen: false,
    }));
  };

  /**
   * Close search form
   *
   * This is handling animations manually,
   * need to update to a more react friendly way of doing this.
   * need to remove static setTimeout delay
   */
  closeSearchForm = () => {
    // animate the search input closing
    this.setState({
      animateClose: true,
    });
    // wait for the `close animation` to finish before updating state
    setTimeout(() => {
      toggleBodyScrolling(true);
      this.setState({
        isSearchOpen: false,
      });
    }, 600);
  };

  /**
   * Show/hide search form
   */
  toggleSearchIsActive = () => {
    this.setState((state: State) => ({
      ...state,
      isLoginOpen: false,
      animateClose: false,
      isSearchOpen: !state.isSearchOpen,
    }));
  };

  /**
   * Close login form
   */
  closeLoginForm = () =>
    this.setState({
      isLoginOpen: false,
    });

  /**
   * Show/hide login form
   */
  toggleLoginIsActive = (analytics: Analytics, location: string) => {
    this.setState(
      (state: State) => ({
        ...state,
        isNavOpen: false,
        isLoginOpen: !state.isLoginOpen,
      }),
      // callback
      () => {
        this.closeSearchForm();
      }
    );
  };

  /**
   * Close login modal
   */
  closeLoginModal = () =>
    this.setState({
      showLoginModal: false,
    });

  /*
   * Renders main menu
   */
  renderMainMenu = (
    isVisible: boolean,
    analytics: Analytics,
    location: string
  ) => (
    <React.Fragment>
      <div
        ref={(el: HTMLElement | null) => {
          this.topMenuItems = el;
        }}
        className={classnames('top-menu-items', {
          'is-open': this.state.isNavOpen,
          'is-visible': isVisible,
        })}
      >
        {this.props.navTreeData.items.map((menuItem: TopMenuItemType) => {
          const isActive = this.state.activeMenuId === menuItem.id;
          return (
            <TopMenuItem
              key={menuItem.id}
              {...menuItem}
              active={isActive}
              onClick={() => {
                this.handleMenuItemClick(menuItem, analytics, location);
              }}
            >
              {this.renderSubMenu(menuItem.id, analytics, location)}
            </TopMenuItem>
          );
        })}

        {/* mobile only */}
        <MediaQuery maxWidth={MOBILE_CUTOFF_POINT}>
          {this.renderMobilePane(analytics, location)}
        </MediaQuery>
      </div>

      {/* mobile only */}
      <MediaQuery maxWidth={MOBILE_CUTOFF_POINT}>
        <div className="top-menu-items__overlay" />
      </MediaQuery>
    </React.Fragment>
  );

  /**
   * Renders the 2nd level menu with the given Id
   */
  renderSubMenu = (
    menuId: string,
    analytics: Analytics,
    location: string
  ): React.Node => {
    const menu = this.props.navTreeData.items.find(
      (item: TopMenuItemType) => item.id === menuId
    );
    if (!menu) return null;
    return (
      <div className="menu-items">
        {/* sub menu is fixed to have three columns */}
        {[0, 1, 2].map((columnIndex: number) => {
          // If we have menu items in this column, render them
          if (menu.items && menu.items.length > columnIndex) {
            const column = menu.items[columnIndex];
            return (
              <div key={columnIndex} className="menu-items__group">
                {column.map((menuItem: MenuItemType) => (
                  <MenuItem
                    key={menuItem.id}
                    {...menuItem}
                    indentLeftMobile={!menuItem.labelOnly}
                    active={this.state.activeMenuId === menuItem.id}
                    onClick={() =>
                      this.handleMenuItemClick(menuItem, analytics, location)
                    }
                    desktopOnly={menuItem.desktopOnly}
                    mobileOnly={menuItem.mobileOnly}
                  />
                ))}
                {/* Render the marketing banner (if any) in the last column */}
                {columnIndex === 2 && menu.banner && (
                  <MediaQuery minWidth={DESKTOP_CUTOFF_POINT}>
                    {this.props.renderBanner(menu.banner)}
                  </MediaQuery>
                )}
              </div>
            );
          }

          // If we are in the empty 3rd column and there is a banner, then render the banner
          if (columnIndex === 2) {
            return (
              <div key={columnIndex} className="menu-items__group">
                {menu.banner && (
                  <MediaQuery minWidth={DESKTOP_CUTOFF_POINT}>
                    {this.props.renderBanner(menu.banner, columnIndex)}
                  </MediaQuery>
                )}
              </div>
            );
          }
        })}
      </div>
    );
  };

  /**
   * Renders the mobile-only pane
   */
  renderMobilePane = (analytics: Analytics, location: string) => (
    <div className="menu-items__group menu-items__group--calculators">
      {this.props.navTreeData.mobilePane.items.map((menuItem: MenuItemType) => (
        <MenuItem
          key={menuItem.id}
          {...menuItem}
          active={this.state.activeMenuId === menuItem.id}
          onClick={() =>
            this.handleMenuItemClick(menuItem, analytics, location)
          }
        />
      ))}
    </div>
  );

  /**
   * Render the main navigation bar
   */
  renderNavigationBar = (analytics: Analytics, location: string) => (
    <nav
      className="site-header__nav"
      itemScope
      itemType="http://schema.org/SiteNavigationElement"
    >
      <HamburgerButton
        ref={(el: HTMLElement | null) => {
          this.hamburgerButton = el;
        }}
        hamburgerButtonRef
        navOpen={this.state.isNavOpen}
        onClick={() => this.toggleNavOpen(analytics, location)}
      />
      {this.props.renderBrandButton(!this.state.isSearchOpen)}
      {this.renderMainMenu(!this.state.isSearchOpen, analytics, location)}
    </nav>
  );

  render() {
    const analytics = {};

    return (
      <AnalyticsLocationProvider location={ANALYTICS.LOCATION.HEADER}>
        <AnalyticsContext.Consumer>
          {([analytics]: [Analytics]) => (
            <AnalyticsLocationConsumer>
              {(location) => (
                <header
                  className={classnames('site-header', 'site-base-header', {
                    'active-header': this.state.isNavBackgroundVisible,
                  })}
                  data-sticky-navigation-offset
                >
                  <BrowserSupport />
                  <div className="site-header__inner">
                    <div className="site-header__nav-wrap no-padding-mob">
                      {this.renderNavigationBar(analytics, location)}
                      <SearchForm
                        analytics={analytics}
                        active={this.state.isSearchOpen}
                        animateClose={this.state.animateClose}
                        onRequestClose={() => {
                          this.closeSearchForm();
                          trackEvent(analytics, {
                            category: 'search',
                            action: 'interaction',
                            label: 'close',
                            location,
                          });
                        }}
                        searchFormData={this.props.searchFormData}
                      />
                      {/* <SearchButton
                        visible={!this.state.isSearchOpen}
                        onClick={() => {
                          this.toggleSearchIsActive();
                          trackEvent(analytics, {
                            category: 'search',
                            action: 'interaction',
                            label: 'open',
                            location,
                          });
                        }}
                      /> */}
                      <LogonButton
                        ref={(el: HTMLElement | null) => {
                          this.logonButtonElement = el;
                        }}
                        active={this.state.isLoginOpen}
                        mobileVisible={!this.state.isSearchOpen}
                        onClick={() =>
                          this.toggleLoginIsActive(analytics, location)
                        }
                        label={this.props.logonButtonLabel}
                      >
                        {/* Show login modal on certain URLs e.g. ...?showLoginModal=activateinfinitycard */}
                        <LoginModal
                          isOpen={
                            this.state.showLoginModal && !this.state.isLoginOpen
                          }
                          data={this.state.loginModalData}
                          onRequestClose={this.closeLoginModal}
                          renderLoginModalImage={
                            this.props.renderLoginModalImage
                          }
                          setImageModuleSyntax={this.props.setImageModuleSyntax}
                        />
                      </LogonButton>
                      <LoginForm
                        analytics={analytics}
                        loginForm={this.props.loginFormData}
                        cardTypes={this.props.cardTypesData}
                        isOpen={this.state.isLoginOpen}
                        onRequestClose={this.closeLoginForm}
                        openElement={this.logonButtonElement}
                        showInsurancePane={this.props.showInsurancePane}
                        showLpayPane={this.props.showLpayPane}
                        showLoansPane={this.props.showLoansPane}
                        showCardsPane={this.props.showCardsPane}
                      />
                    </div>
                  </div>
                </header>
              )}
            </AnalyticsLocationConsumer>
          )}
        </AnalyticsContext.Consumer>
      </AnalyticsLocationProvider>
    );
  }
}

export default BaseHeader;
